import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMenu, FiX, FiArrowUp, FiHome, FiUser, FiCode, FiBriefcase, FiSend, FiGrid, FiGithub, FiExternalLink } from 'react-icons/fi';

const navItems = [
  { name: 'Home', to: 'home', icon: FiHome },
  { name: 'About', to: 'about', icon: FiUser },
  { name: 'Skills', to: 'skills', icon: FiCode },
  { name: 'Projects', to: 'projects', icon: FiBriefcase },
  { name: 'Contact', to: 'contact', icon: FiSend },
];

const extraLinks = [
  { name: 'Labs', to: '/labs', icon: FiGrid, desc: 'UI/UX & Experiments' },
  { name: 'GitHub', href: 'https://github.com/evodenuby', icon: FiGithub, desc: 'Developer Credibility' },
];

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

const Navbar: React.FC = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';

  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return true;
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isHome) return;
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 10);
      setShowBackToTop(y > 300);

      const current = navItems.map(n => n.to).find(s => {
        const el = document.getElementById(s);
        return el && el.getBoundingClientRect().top <= 100 && el.getBoundingClientRect().bottom >= 100;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    if (isHome) return;
    window.location.href = `/#${id}`;
  };

  return (
    <>
      {/* Top accent line */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[2px] bg-gradient-to-r from-blue-500 to-purple-500 opacity-80" />

      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={`fixed top-[2px] left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-primary/85 backdrop-blur-2xl border-b border-border shadow-lg shadow-black/5'
            : 'bg-primary/60 backdrop-blur-lg'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <RouterLink
              to="/"
              className="flex items-center gap-3 group cursor-pointer"
            >
              <div className="relative">
                <img
                  src="./logo1.png"
                  alt="EVODENUBY"
                  className="h-8 w-8 md:h-9 md:w-9 object-contain transition-all duration-300 group-hover:scale-110 group-hover:brightness-110"
                />
                <div className="absolute -inset-2 bg-secondary/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <span className="font-mono text-sm md:text-base font-bold tracking-tight text-textPrimary group-hover:text-secondary transition-colors">
                EVODENUBY<span className="text-secondary">.</span>
              </span>
            </RouterLink>

            {/* Desktop Right Section */}
            <div className="hidden md:flex items-center gap-1">
              {/* Main nav */}
              <div className="flex items-center">
                {navItems.map((item) => {
                  const active = isHome && activeSection === item.to;
                  if (!isHome) {
                    return (
                      <button
                        key={item.to}
                        onClick={() => scrollToSection(item.to)}
                        className="relative px-2.5 py-1.5 text-xs font-medium tracking-wide uppercase rounded-md transition-colors cursor-pointer text-textSecondary hover:text-secondary"
                      >
                        {item.name}
                      </button>
                    );
                  }
                  return (
                    <ScrollLink
                      key={item.to}
                      to={item.to}
                      spy={true}
                      smooth={true}
                      offset={-100}
                      duration={800}
                      onSetActive={(t: string) => setActiveSection(t)}
                      className={`relative px-2.5 py-1.5 text-xs font-medium tracking-wide uppercase rounded-md transition-colors cursor-pointer ${
                        active ? 'text-secondary' : 'text-textSecondary hover:text-secondary'
                      }`}
                    >
                      {item.name}
                      {active && (
                        <motion.div
                          layoutId="underline"
                          className="absolute -bottom-0.5 left-2 right-2 h-[2px] bg-secondary rounded-full"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </ScrollLink>
                  );
                })}
              </div>

              {/* Divider + Extra links */}
              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-border/50">
                {extraLinks.map((link) => {
                  if ('href' in link) {
                    return (
                      <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer">
                        <span className="group flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer text-textSecondary/70 hover:text-secondary hover:bg-tertiary/50" title={link.desc}>
                          {Icon(link.icon, 'text-[13px]')}
                          {link.name}
                          <span className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
                            {Icon(FiExternalLink, 'text-[10px]')}
                          </span>
                        </span>
                      </a>
                    );
                  }

                  return (
                    <RouterLink
                      key={link.name}
                      to={link.to}
                    >
                      <span
                        className={`group flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                          location.pathname === link.to
                            ? 'text-secondary'
                            : 'text-textSecondary/70 hover:text-secondary hover:bg-tertiary/50'
                        }`}
                        title={link.desc}
                      >
                        {Icon(link.icon, 'text-[13px]')}
                        {link.name}
                      </span>
                    </RouterLink>
                  );
                })}

                {/* Theme Toggle */}
                <div className="ml-2 pl-2 border-l border-border/50">
                  <button
                    onClick={() => setIsDark((p) => !p)}
                    className="p-1.5 rounded-md text-textSecondary/70 hover:text-secondary hover:bg-tertiary/50 transition-all"
                    aria-label={isDark ? 'Light mode' : 'Dark mode'}
                  >
                    {Icon(isDark ? FiSun : FiMoon, 'text-sm')}
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile right */}
            <div className="flex md:hidden items-center gap-0.5">
              <button
                onClick={() => setIsDark((p) => !p)}
                className="p-1.5 rounded-md text-textSecondary hover:text-secondary transition-colors"
                aria-label={isDark ? 'Light mode' : 'Dark mode'}
              >
                {Icon(isDark ? FiSun : FiMoon, 'text-sm')}
              </button>
              <button
                onClick={() => setMenuOpen((p) => !p)}
                className="p-1.5 rounded-md text-textSecondary hover:text-secondary transition-colors"
                aria-label={menuOpen ? 'Close' : 'Menu'}
              >
                {Icon(menuOpen ? FiX : FiMenu, 'text-base')}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 top-16 z-40 bg-primary/95 backdrop-blur-2xl md:hidden"
            onClick={() => setMenuOpen(false)}
          >
            <div className="p-4 space-y-1" onClick={(e) => e.stopPropagation()}>
              {/* Main sections */}
              <div className="mb-3 pb-3 border-b border-border/50">
                <div className="px-3 py-1.5 text-[10px] font-mono font-medium uppercase tracking-widest text-textSecondary/40">
                  Sections
                </div>
                {navItems.map((item, i) => {
                  const active = isHome && activeSection === item.to;
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      {isHome ? (
                        <ScrollLink
                          to={item.to}
                          spy={true}
                          smooth={true}
                          offset={-100}
                          duration={800}
                          onSetActive={(t: string) => setActiveSection(t)}
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                            active
                              ? 'text-secondary bg-secondary/10'
                              : 'text-textSecondary hover:text-secondary hover:bg-tertiary/50'
                          }`}
                        >
                          {Icon(item.icon, 'text-sm')}
                          {item.name}
                        </ScrollLink>
                      ) : (
                        <button
                          onClick={() => { setMenuOpen(false); scrollToSection(item.to); }}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-textSecondary hover:text-secondary hover:bg-tertiary/50 w-full text-left"
                        >
                          {Icon(item.icon, 'text-sm')}
                          {item.name}
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              {/* Extra links */}
              <div>
                <div className="px-3 py-1.5 text-[10px] font-mono font-medium uppercase tracking-widest text-textSecondary/40">
                  Explore
                </div>
                {extraLinks.map((link, i) => {
                  if ('href' in link) {
                    return (
                      <motion.div
                        key={link.name}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (navItems.length + i) * 0.03 }}
                      >
                        <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}>
                          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-textSecondary hover:text-secondary hover:bg-tertiary/50">
                            {Icon(link.icon, 'text-sm')}
                            <div className="flex flex-col">
                              <span>{link.name}</span>
                              <span className="text-[10px] font-mono text-textSecondary/40">{link.desc}</span>
                            </div>
                            {Icon(FiExternalLink, 'text-xs ml-auto text-textSecondary/30')}
                          </div>
                        </a>
                      </motion.div>
                    );
                  }

                  return (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (navItems.length + i) * 0.03 }}
                    >
                      <RouterLink to={link.to} onClick={() => setMenuOpen(false)}>
                        <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          location.pathname === link.to
                            ? 'text-secondary bg-secondary/10'
                            : 'text-textSecondary hover:text-secondary hover:bg-tertiary/50'
                        }`}>
                          {Icon(link.icon, 'text-sm')}
                          <div className="flex flex-col">
                            <span>{link.name}</span>
                            <span className="text-[10px] font-mono text-textSecondary/40">{link.desc}</span>
                          </div>
                        </div>
                      </RouterLink>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back to Top — only on home page */}
      {isHome && (
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="fixed bottom-6 right-6 z-40 p-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white backdrop-blur-md border-0 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300"
              aria-label="Back to top"
            >
              {Icon(FiArrowUp, 'text-sm')}
            </motion.button>
          )}
        </AnimatePresence>
      )}
    </>
  );
};

export default Navbar;
